import React, { useEffect, useRef, useState } from 'react'
import { Services } from '@/services/services';
import { SuperAdminEditMachineTypeForm, SuperAdminInsertMachineTypeForm, MachineTypeFormData } from '@/components/forms/machinetypes/MachineType';
import { CurrentSession } from '@/services/repository/session';
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout';
import { addServerErrors } from '@/components/forms/common/FormBase';
import { useForm } from 'react-hook-form';
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable';
import { useModal } from '@/components/modal/Modal';
import { isDesktopScreen, isLargeScreen, useResponsiveChange } from '@/common/device';
import { BackButton } from '@/components/layout/NavBar';

export interface SimpleMachineType {
  id: string
  manufacturer: string
  category: string
  model: string
}

export interface SuperAdminInsertMachineTypePopupProps {
  services: Services,
  session: CurrentSession,
  onClose: () => void
}

export interface SuperAdminEditMachineTypePopupProps extends SuperAdminInsertMachineTypePopupProps {
  id: string | undefined
}

export const SuperAdminEditMachineTypePopup = ({ services, session, onClose, id }: SuperAdminEditMachineTypePopupProps) => {
  const form = useForm<MachineTypeFormData>()
  const [data, setData] = useState<MachineTypeFormData>()

  useEffect(() => {
    if (id === undefined) {
      return
    }
    setData(undefined)
    services.session.fetchWithToken(`/api/communal/machine_types/byid/${id}`).then((r) => r.json()).then((data) => {
      setData(data)
      form.reset(data)
    })
  }, [id]);

  const handle = (data: MachineTypeFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    return services.session.fetchWithToken(`/api/communal/machine_types/byid/${data.id}/update`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }).then(async (r) => {
      if (r.status !== 200) {
        const errors = await r.json()
        addServerErrors(form, session.translates, errors)
        return
      }
      onClose()
    })
  }

  return data !== undefined ? <SuperAdminEditMachineTypeForm
    onSubmit={handle}
    services={services}
    form={form}
    translates={session.translates}
    formName='machinetype'
    readOnly={false}
  /> : <div className="spinner"></div>
}

// export interface SuperAdminInsertMachineTypePopupFNProps extends SuperAdminInsertMachineTypePopupProps  {}

export const SuperAdminInsertMachineTypePopup = ({ services, session, onClose }: SuperAdminInsertMachineTypePopupProps) => {
  const form = useForm<MachineTypeFormData>()

  const handle = (data: MachineTypeFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    delete data.id
    return services.session.fetchWithToken(`/api/communal/machine_types/new`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(async (r) => {
      if (r.status !== 200) {
        const errors = await r.json()
        addServerErrors(form, session.translates, errors)
        return
      }
      form.reset()
      onClose()
    })
  }

  return <SuperAdminInsertMachineTypeForm
    onSubmit={handle}
    services={services}
    form={form}
    translates={session.translates}
    formName='machinetype'
    readOnly={false}
  />
}

export const SuperAdminMachineTypesPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()

  useResponsiveChange()

  const tableParams: SearchTableParams<SimpleMachineType> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleMachineType>> => {
      return services.session.fetchWithToken(`/api/communal/machine_types/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <>
      <div className='tool' onClick={() => openModal((props) => SuperAdminInsertMachineTypePopup({ ...props, services, session }))}><i className='svgfont-add' /></div>
    </>,
    header: () => <>
      <div className='hcol'>{session.translates.for('elements.table.machine_types.manufacturer')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machine_types.model')}</div>
      {isDesktopScreen() && <div className='hcol'>{session.translates.for('elements.table.machine_types.category')}</div>}
      <div className='hcol'></div>
    </>,
    row: (row) => <>
      <div className='col'>{row.manufacturer}</div>
      <div className='col'>{row.model}</div>
      {isDesktopScreen() && <div className='col'>{session.translates.for(`machine.types.${row.category}`)}</div>}
      <div className='col icocol' onClick={() => openModal((props) => SuperAdminEditMachineTypePopup({ ...props, id: row.id, services, session }))}>
        <i className='svgfont-edit' />
      </div>
    </>,
    translates: session.translates
  }

  return <LoggedInPageLayout pageName='superadmin-machine-types' services={services} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>
    <SearchTable ref={searchTableRef} {...tableParams} />
  </LoggedInPageLayout>
}